import { useEffect, useState, memo, useMemo, useCallback } from 'react';
import { Button, CircularProgress, IconButton, Typography, Paper, Box, useTheme } from '@mui/material';
import Close from '@mui/icons-material/Close';
import { TOAST_LIFETIME } from '@/common/timeout_constants';
import { PersistentNotification } from '../types';
import { CopyDebugInfoButton } from './CopyDebugInfoButton';
import { dispatchToastAction } from '../actionRegistry';
import { getSeverityStyles, SEVERITY_ICONS, NotificationSeverity } from '../utils/notification_styles';
import { SecondaryActionLink } from './SecondaryActionLink';

export type NotificationPosition = 'top-right' | 'bottom-right' | 'top-center';

type ToastProps = {
  notification: PersistentNotification;
  onClose: () => void;
  autoHideDuration?: number;
  position?: NotificationPosition;
};

export const ToastWithProgress = memo(function ToastWithProgress({
  notification,
  onClose,
  autoHideDuration = TOAST_LIFETIME,
  position = 'top-right',
}: ToastProps) {
  const { message, severity, manualDismiss, waiting, debugInfo, sessionId, accountId, created, action, secondaryAction } =
    notification;
  const [isHovered, setIsHovered] = useState(false);
  const [isWindowHidden, setIsWindowHidden] = useState(() => (typeof document !== 'undefined' ? document.hidden : false));
  const theme = useTheme();

  const {
    text: textColor,
    border: borderColor,
    background: backgroundColor,
    backdropFilter,
    boxShadow,
  } = useMemo(() => getSeverityStyles(severity as NotificationSeverity, theme), [severity, theme]);

  const icon = useMemo(
    () => (waiting ? <CircularProgress disableShrink size={20} /> : SEVERITY_ICONS[severity as NotificationSeverity]),
    [severity, waiting]
  );

  const isPaused = isHovered || isWindowHidden;
  const shouldShowCopyButton = (debugInfo || severity === 'error') && (debugInfo || sessionId || accountId != null);

  const handleActionClick = useCallback(() => {
    if (!action) return;
    dispatchToastAction(action);
    onClose();
  }, [action, onClose]);

  const handleSecondaryActionClick = useCallback(() => {
    if (!secondaryAction) return;
    dispatchToastAction(secondaryAction);
    onClose();
  }, [secondaryAction, onClose]);

  useEffect(() => {
    if (manualDismiss || typeof document === 'undefined') return;

    const handleVisibilityChange = () => {
      setIsWindowHidden(document.visibilityState === 'hidden');
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [manualDismiss]);

  const toastStyles = {
    color: textColor,
    p: 1,
    gap: 1,
    border: '1px solid',
    borderColor: borderColor,
    backgroundColor: backgroundColor || 'background.paper',
    backdropFilter: backdropFilter || '',
    ...(boxShadow ? { boxShadow } : {}),
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    position: 'relative',
    overflow: 'hidden',
    minWidth: position === 'top-center' ? 400 : 300,
    maxWidth: position === 'top-center' ? 600 : 500,
  };

  return (
    <Paper
      elevation={6}
      sx={toastStyles}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      role={severity === 'error' ? 'alert' : 'status'}
      aria-live={severity === 'error' ? 'assertive' : 'polite'}
    >
      <Box p={0.5} display="flex" alignItems="center">
        {icon}
      </Box>
      <Typography variant="body2" component="div" sx={{ flex: 1, alignSelf: 'center' }}>
        {message}
        {secondaryAction && <SecondaryActionLink action={secondaryAction} onClick={handleSecondaryActionClick} />}
      </Typography>
      {action && (
        <Button
          size="small"
          variant="contained"
          color={severity === 'announcement' ? 'inherit' : severity}
          onClick={handleActionClick}
          sx={{ alignSelf: 'center', whiteSpace: 'nowrap', flexShrink: 0 }}
        >
          {action.label}
        </Button>
      )}
      {shouldShowCopyButton && (
        <CopyDebugInfoButton
          message={typeof message === 'string' ? message : ''}
          debugInfo={debugInfo}
          sessionId={sessionId}
          accountId={accountId}
          timestamp={created}
          color="inherit"
          sx={{ p: 0.5 }}
        />
      )}
      <IconButton size="small" aria-label="close" color="inherit" onClick={onClose} sx={{ p: 0.5, alignSelf: 'center' }}>
        <Close fontSize="small" />
      </IconButton>
      {!manualDismiss && (
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: '3px',
            overflow: 'hidden',
          }}
        >
          <div
            onAnimationEnd={onClose}
            style={{
              backgroundColor: borderColor,
              animation: `toastProgressShrink ${autoHideDuration}ms linear forwards`,
              transformOrigin: 'left',
              height: '100%',
              width: '100%',
              animationPlayState: isPaused ? 'paused' : 'running',
            }}
          />
        </div>
      )}
    </Paper>
  );
});
